import { Camera, ImagePlus } from 'lucide-react'
import { useEffect, useState, type SyntheticEvent } from 'react'

import { Button, Sheet } from '../../components/ui'
import type { SessionView } from '../../domain/access'
import { processImage } from '../../services/imagePipeline'
import { albumRepository } from '../album/albumService'

export function DailyPhotoCapture({ productDay, session, onAdded, onClose }: {
  productDay: string
  session: SessionView
  onAdded: () => void
  onClose: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }
  }, [previewUrl])

  function chooseFile(nextFile: File | null) {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setFile(nextFile)
    setPreviewUrl(nextFile ? URL.createObjectURL(nextFile) : null)
  }

  async function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!file) { setError('Choisis une photo.'); return }
    setBusy(true)
    setError(null)
    try {
      await albumRepository.addPhoto({
        tripId: session.tripId,
        memberId: session.memberId,
        displayName: session.displayName,
        purpose: 'daily_candidate',
        productDay,
        caption: '',
        createdAtDevice: new Date().toISOString(),
        fileName: file.name,
      }, await processImage(file))
      onAdded()
      onClose()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'La photo n’a pas pu être ajoutée.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet title="Photo du jour" onClose={onClose}>
      <form className="daily-photo-capture" onSubmit={(event) => void submit(event)}>
        {previewUrl ? <img alt="Aperçu de la photo sélectionnée" src={previewUrl} /> : <div className="daily-photo-capture__empty"><ImagePlus size={30} /><span>Choisis une photo</span></div>}
        <div className="daily-photo-capture__choices">
          <label className="button button--secondary" htmlFor="daily-photo-library"><ImagePlus size={18} /> Galerie</label>
          <label className="button button--secondary" htmlFor="daily-photo-camera"><Camera size={18} /> Appareil photo</label>
          <input accept="image/jpeg,image/png,image/webp" className="visually-hidden" id="daily-photo-library" type="file" onChange={(event) => chooseFile(event.target.files?.[0] ?? null)} />
          <input accept="image/jpeg,image/png,image/webp" capture="environment" className="visually-hidden" id="daily-photo-camera" type="file" onChange={(event) => chooseFile(event.target.files?.[0] ?? null)} />
        </div>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <Button disabled={!file || busy} fullWidth type="submit">{busy ? 'Ajout en cours…' : 'Valider cette photo'}</Button>
      </form>
    </Sheet>
  )
}
